import { useState, useEffect, useCallback } from 'react';
import { getCurrentCashRegister, getOpenRegistersContext, getMyAssignedStations } from '../services/api';

/**
 * Custom hook to manage cash register status.
 *
 * Fetches in parallel:
 *  1. User's own open register session (GET /cash-register/current)
 *  2. All open registers in the business (GET /cash-register/open-context)
 *  3. Stations the user is responsible for (GET /cash-register/my-stations)
 *
 * @returns {Object} {
 *   isOpen,          – user has their own open register session
 *   cashRegister,    – the user's open register object (or null)
 *   openContext,     – { count, registers } for the whole business
 *   myStations,      – stations the user is responsible for
 *   canOpenAStation, – true if a responsible station is currently closed
 *   loading,
 *   error,
 *   refetch
 * }
 */
const useCashRegisterStatus = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [cashRegister, setCashRegister] = useState(null);
    const [openContext, setOpenContext] = useState({ count: 0, registers: [] });
    const [myStations, setMyStations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [currentRes, contextRes, stationsRes] = await Promise.all([
                getCurrentCashRegister().catch(() => ({ data: { isOpen: false, cashRegister: null } })),
                getOpenRegistersContext().catch(() => ({ data: { count: 0, registers: [] } })),
                getMyAssignedStations().catch(() => ({ data: [] })),
            ]);

            setIsOpen(currentRes.data.isOpen);
            setCashRegister(currentRes.data.cashRegister);
            setOpenContext(contextRes.data);
            setMyStations(stationsRes.data);
        } catch (err) {
            console.error('Error fetching cash register status:', err);
            setError(err);
            setIsOpen(false);
            setCashRegister(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    // True if the user has at least one responsible station that is currently closed
    const canOpenAStation = myStations.some(s => !s.is_open);

    return {
        isOpen,
        cashRegister,
        openContext,
        myStations,
        canOpenAStation,
        loading,
        error,
        refetch: fetchStatus
    };
};

export default useCashRegisterStatus;
